import { useCallback, useEffect, useState } from "react";
import { Download, FilePlus2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, Th, Td, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

export const dlIdoc = async (f) => {
  try {
    const r = await api.get(`/sap/idoc-files/${f.id}/download`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = f.filename;
    a.click();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const badge = (s) => (s === "sent" || s === "collected" ? "sent" : s === "test_hold" ? "test_hold" : s === "duplicate_hold" ? "error" : "ready");

export const IdocRows = ({ rows, onChanged, showPo }) => {
  const { user } = useAuth();
  const canDelete = ["admin", "manager"].includes(user?.role);
  const release = (f) => (f.status !== "duplicate_hold" || window.confirm(`PO ${f.po_number} already has another IDoc. Releasing ${f.filename} lets SAP collect it and may create a duplicate sales order. Release anyway?`)) && api.post(`/sap/idoc-files/${f.id}/release`).then(() => { toast.success(`${f.filename} queued for SAP SFTP`); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  const del = (f) => {
    const warn = f.status === "collected" || f.status === "sent"
      ? `${f.filename} has already been collected into the SAP folder. Deleting it here does NOT remove it from SAP or the folder. Delete it from the Hub anyway?`
      : `Delete ${f.filename} for PO ${f.po_number}? It will no longer be picked up by SAP.`;
    if (!window.confirm(warn)) return;
    api.delete(`/sap/idoc-files/${f.id}`).then(() => { toast.success(`${f.filename} deleted`); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-slate-800"><tr><Th>Created</Th><Th>File</Th>{showPo && <Th>PO</Th>}<Th>Partner</Th><Th>Sold-to / ship-to</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {rows.map((f) => (
            <tr key={f.id} data-testid={`idoc-row-${f.id}`}>
              <Td className="whitespace-nowrap font-mono text-slate-500">{ts(f.created_at)}</Td>
              <Td className="font-mono text-emerald-300" data-testid={`idoc-file-${f.id}`}>{f.filename}</Td>
              {showPo && <Td className="font-mono">{f.po_number}</Td>}
              <Td>{f.partner_name}</Td><Td className="font-mono">{f.sold_to} / {f.ship_to}</Td>
              <Td><StatusBadge status={badge(f.status)} label={f.status === "duplicate_hold" ? "duplicate - held" : f.status} />{f.error && <span className="ml-2 text-xs text-rose-300">{f.error}</span>}</Td>
              <Td className="whitespace-nowrap text-right">
                {["test_hold", "duplicate_hold"].includes(f.status) && <Button size="sm" variant="ghost" className={`${btn.ghost} mr-1`} onClick={() => release(f)} data-testid={`idoc-release-${f.id}`}><Send className="mr-1 h-3.5 w-3.5" /> Send to SAP</Button>}
                <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => dlIdoc(f)} data-testid={`idoc-download-${f.id}`}><Download className="h-3.5 w-3.5" /></Button>
                {canDelete && <Button size="sm" variant="ghost" className={`${btn.ghost} text-rose-300`} onClick={() => del(f)} title="Delete IDoc" data-testid={`idoc-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const OrderIdocPanel = ({ o, onChanged }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/orders/${o.id}/idoc`).then((r) => setRows(r.data)), [o.id]);
  useEffect(() => { load(); }, [load, o.idoc_file]);
  if (o.order_type !== "customer") return null;
  const create = () => {
    if (rows.length && !window.confirm("This order already has an SAP IDoc. The new one will be held as a duplicate until you release it. Continue?")) return;
    api.post(`/orders/${o.id}/idoc`).then((r) => { toast[rows.length ? "warning" : "success"](`SAP IDoc ${r.data.filename} created${rows.length ? " - held as duplicate, not collected until released" : ""}`); load(); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <Panel title="SAP order IDoc" testId="order-idoc-panel"
      action={<Button size="sm" variant="ghost" onClick={create} className={btn.ghost} data-testid="order-idoc-create-btn"><FilePlus2 className="mr-1 h-3.5 w-3.5" /> {rows.length ? "Re-create SAP IDoc" : "Create SAP IDoc"}</Button>}>
      {o.idoc_status === "error" && <div className="border-b border-slate-800 px-5 py-3 text-xs text-amber-300" data-testid="order-idoc-error">Not created: {o.idoc_error}</div>}
      {rows.length ? <IdocRows rows={rows} onChanged={() => { load(); onChanged(); }} /> : <p className="px-5 py-4 text-xs text-slate-500" data-testid="order-idoc-empty">No IDoc for this order. IDocs are made automatically when IDoc output is switched on for the trading partner.</p>}
    </Panel>
  );
};
